import React from "react";
import EmptyChart from "../ui/EmptyChart";

/**
 * Sparkline — minimal SVG line chart with a soft gradient fill.
 *
 * Used by:
 *   - CallsPerDayCard (call volume over time)
 *   - AssistantPerformanceCard (p95 TTFT per day)
 *
 * Props:
 *   points: number[]   — Y values in order; X is implicit (evenly spaced)
 *   max:    number     — Y-axis max; usually `Math.max(1, ...points)`
 *   ariaLabel?: string — accessibility label for the <svg>
 *
 * Renders an `EmptyChart` placeholder when `points` is empty.
 */
const Sparkline = ({ points, max, ariaLabel = "Sparkline" }) => {
  const W = 600;
  const H = 120;
  const pad = 4;

  if (!points || points.length === 0) {
    return <EmptyChart label="No data in range" height={H} />;
  }

  const step = points.length > 1 ? (W - pad * 2) / (points.length - 1) : 0;
  const y = (v) => H - pad - (v / max) * (H - pad * 2);
  const path = points
    .map((p, i) => `${i === 0 ? "M" : "L"} ${pad + i * step} ${y(p)}`)
    .join(" ");
  const areaPath = `${path} L ${pad + (points.length - 1) * step} ${H - pad} L ${pad} ${H - pad} Z`;

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      className="w-full h-32"
      role="img"
      aria-label={ariaLabel}
    >
      <defs>
        <linearGradient id="spark-fill" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.25" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
        </linearGradient>
      </defs>
      <g className="text-brand">
        <path d={areaPath} fill="url(#spark-fill)" />
        <path d={path} fill="none" stroke="currentColor" strokeWidth="2" />
        {points.map((p, i) => (
          <circle
            key={i}
            cx={pad + i * step}
            cy={y(p)}
            r={2.5}
            fill="currentColor"
          />
        ))}
      </g>
    </svg>
  );
};

export default Sparkline;
